import { useEffect, useRef, useState } from 'react'
import { KeyRound, ShieldCheck, Smartphone, X } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useI18n } from '../i18n/useI18n'
import { showToast } from './Toast'
import { auth } from '../lib/firebase'
import { startTotpEnroll, completeTotpEnroll } from '../lib/totpMfa'
import { api } from '../services/api'
import type { TotpEnrollStart } from '../lib/totpMfa'

/**
 * 認証アプリ未導入ユーザー向け store リンク (#815 follow-up)。
 * OS を userAgent で判定し Google Authenticator の直リンクを返す
 * (`totp.appHint` の第一候補と一致)。「未インストールを自動検知して
 * store に飛ばす」は Web で確実にできない (otpauth:// の成否を判定する
 * 標準 API が無い) ため、deep-link ボタンの直下に本リンクを**常時表示**
 * して dead-end を作らない設計。iOS=App Store / それ以外=Play (Android +
 * desktop fallback。desktop は QR を使うので install リンクは実害なし)。
 */
function authenticatorInstallUrl(): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''
  if (/iPad|iPhone|iPod/.test(ua)) {
    return 'https://apps.apple.com/app/google-authenticator/id388497605'
  }
  return 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2'
}

interface Props {
  open: boolean
  /** TOTP enroll 完了時 (= gate を満たす). */
  onEnrolled: () => void
  /**
   * #1104: 指定時は dismissible (= 設定画面からの任意登録)。未指定 (=
   * RequireAuth/Login の enforced gate) では閉じるボタンを出さない従来挙動。
   */
  onClose?: () => void
}

/**
 * WebAuthn 不可層 (#787) 向け TOTP MFA 登録モーダル (#815 / #789 decision).
 *
 * `RequireAuth` が enrollment 必須かつ WebAuthn 不可ブラウザ + flag ON の時に
 * **全画面 block で強制表示**（PasskeyEnrollmentModal の enforced と同様、
 * close/skip 不可）。guided onboarding: 「不正決済から守る」理由 + 操作手順 +
 * QR（スキャン）/ 手入力キー（旧 device 向け）+ 6 桁コード検証。
 *
 * 強度は #788 ①'②③④ (demo 実証済) が担保。本 modal は不可少数層の
 * identity 継続性のための最小実装（#789 / 過剰実装しない）。
 */
export default function TotpEnrollmentModal({ open, onEnrolled, onClose }: Props) {
  const { t } = useI18n()
  const [start, setStart] = useState<TotpEnrollStart | null>(null)
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [initError, setInitError] = useState(false)
  const startedMetricRef = useRef(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const user = auth.currentUser
    if (!user) {
      setInitError(true)
      return
    }
    // #815 離脱率 metric: enforced modal 到達 = enroll 開始 (best-effort)。
    if (!startedMetricRef.current) {
      startedMetricRef.current = true
      void api.recordTotpEnrollEvent('started').catch(() => {})
    }
    startTotpEnroll(user)
      .then((s) => {
        if (!cancelled) setStart(s)
      })
      .catch((e) => {
        console.error('[TotpEnrollment] start failed:', e)
        if (!cancelled) setInitError(true)
      })
    return () => {
      cancelled = true
    }
  }, [open])

  if (!open) return null

  const handleVerify = async () => {
    if (submitting || !start) return
    const user = auth.currentUser
    if (!user) {
      setInitError(true)
      return
    }
    setSubmitting(true)
    try {
      await completeTotpEnroll(user, start.secret, code.trim())
      void api.recordTotpEnrollEvent('completed').catch(() => {})
      showToast({ type: 'success', message: t('totp.enrolled') })
      onEnrolled()
    } catch (e) {
      const code2 = (e as { code?: string })?.code
      console.error('[TotpEnrollment] verify failed:', e)
      if (code2 === 'auth/invalid-verification-code' || code2 === 'auth/invalid-code') {
        showToast({ type: 'error', message: t('totp.invalidCode') })
      } else if (code2 === 'auth/unverified-email') {
        showToast({ type: 'error', message: t('totp.emailUnverified') })
      } else {
        showToast({ type: 'error', message: t('totp.enrollFailed') })
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
      <div className="relative w-full max-w-sm rounded-2xl bg-white p-6 animate-scale-in max-h-[90vh] overflow-y-auto">
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
            aria-label="close"
          >
            <X size={16} className="text-text-secondary" />
          </button>
        )}
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-yokoso-red/10">
          <KeyRound size={32} className="text-yokoso-red" />
        </div>

        <h3 className="font-display text-xl font-bold text-text-primary text-center mb-2">
          {t('totp.enrollTitle')}
        </h3>
        <p className="font-body text-sm text-text-secondary text-center mb-4 leading-relaxed">
          {t('totp.enrollDescription')}
        </p>

        <ul className="space-y-2 mb-4">
          {['totp.benefit1', 'totp.benefit2', 'totp.benefit3'].map((k) => (
            <li key={k} className="flex items-start gap-2 text-sm font-body text-text-primary">
              <ShieldCheck size={16} className="text-matcha shrink-0 mt-0.5" />
              {t(k)}
            </li>
          ))}
        </ul>

        {initError ? (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-4">
            <p className="text-xs font-body text-amber-900 leading-relaxed">
              {t('totp.enrollFailed')} {t('totp.recoveryContact')}
            </p>
          </div>
        ) : !start ? (
          <p className="text-sm font-body text-text-secondary text-center py-6">…</p>
        ) : (
          <>
            <p className="font-body text-xs text-text-secondary mb-3 leading-relaxed">
              {t('totp.setupInstruction')}
              <br />
              <span className="text-text-primary">{t('totp.appHint')}</span>
            </p>
            <div className="flex justify-center mb-3">
              <div className="p-3 bg-white border border-gray-200 rounded-lg">
                <QRCodeSVG value={start.qrCodeUrl} size={160} />
              </div>
            </div>

            {/* #815 follow-up: 同一端末(スマホ)主導線。タップで otpauth://
                → authenticator アプリ起動(account 自動登録)。未インストール
                時の dead-end を避けるため直下に OS 別 store リンクを常時表示
                (otpauth:// の成否は Web で確実に判定不能なため自動 redirect
                はしない設計判断)。PC は上の QR を別端末で読む。 */}
            <div className="flex items-center gap-3 my-3">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-[11px] font-body text-text-tertiary">
                {t('totp.orSeparator')}
              </span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
            <a
              href={start.qrCodeUrl}
              className="flex items-center justify-center gap-2 w-full h-12 rounded-xl border border-yokoso-red/50 bg-yokoso-red/5 text-yokoso-red font-display font-bold text-sm tap-effect"
            >
              <Smartphone size={16} />
              {t('totp.openAppAction')}
            </a>
            <a
              href={authenticatorInstallUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-center text-xs font-body text-navy-light underline underline-offset-2 mt-2 mb-1"
            >
              {t('totp.noAppInstall')}
            </a>

            <div className="mb-4">
              <span className="block text-xs font-body text-text-secondary mb-1">
                {t('totp.manualKeyLabel')}
              </span>
              <code className="block text-xs font-mono break-all bg-gray-50 border border-gray-200 rounded-lg p-2 text-text-primary select-all">
                {start.manualKey}
              </code>
            </div>
            <label className="block text-xs font-body text-text-secondary mb-1">
              {t('totp.codeInputLabel')}
            </label>
            <input
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className="w-full h-12 mb-4 text-center tracking-[0.4em] text-lg font-mono border border-gray-300 rounded-xl focus:outline-none focus:border-yokoso-red"
              placeholder="000000"
            />
            <button
              onClick={handleVerify}
              disabled={submitting || code.length !== 6}
              className={`w-full h-12 rounded-xl font-display font-bold text-sm transition-colors ${
                submitting || code.length !== 6
                  ? 'bg-gray-300 text-white cursor-not-allowed'
                  : 'bg-yokoso-red text-white tap-effect'
              }`}
            >
              {submitting ? t('totp.enrolling') : t('totp.verifyAction')}
            </button>
          </>
        )}

        <p className="font-body text-[11px] text-text-secondary text-center mt-3 leading-relaxed">
          {t('totp.recoveryNote')}
        </p>
      </div>
    </div>
  )
}
